import { useForm, zodResolver } from '@mantine/form';
import { useEffect } from 'react';
import { z } from 'zod';

import { ADDRESS_COUNTRY_MAX_LENGTH } from '@constants';
import { useSaveGoodLocation } from '@hooks/declarant';

const validateRequiredFields = (values: any, requiredFields: string[], basePath: string, ctx: z.RefinementCtx): void => {
  requiredFields.forEach((field) => {
    if (!values[field]) {
      ctx.addIssue({
        path: [basePath, field],
        message: 'Required',
        code: 'custom',
      });
    }
  });
};

const editGoodsLocationSchema = z
  .object({
    _id: z.string().nullable().optional(),
    defaultLocation: z.boolean().nullable().optional(),
    type: z.string().min(1, { message: 'Required' }),
    qualifierOfIdentification: z.string().min(1, { message: 'Required' }),
    unLocode: z.string().min(1, { message: 'Required' }),
    unLocodeDescription: z.string().nullable().optional(),
    customsOffice: z.object({
      referenceNumber: z.string().min(1, { message: 'Required' }),
    }),
    gnss: z.object({
      latitude: z.union([z.string(), z.number()]).optional().nullable(),
      longitude: z.union([z.string(), z.number()]).optional().nullable(),
    }),
    economicOperator: z.object({
      identificationNumber: z.string().nullable().optional(),
    }),
    authorisationNumber: z.string().nullable().optional(),
    additionalIdentifier: z.string().nullable().optional(),
    address: z.object({
      street: z.string().nullable().optional(),
      postcode: z.string().nullable().optional(),
      city: z.string().nullable().optional(),
      country: z.string().nullable().optional(),
    }),
    postcode: z.object({
      postcode: z.string().nullable().optional(),
      houseNumber: z.string().nullable().optional(),
      country: z.string().nullable().optional(),
    }),
  })
  .superRefine(({ qualifierOfIdentification, postcode, gnss, economicOperator, authorisationNumber, address }, ctx) => {
    switch (qualifierOfIdentification) {
      case 'T': {
        validateRequiredFields(postcode, ['postcode', 'country', 'houseNumber'], 'postcode', ctx);
        break;
      }
      case 'W': {
        validateRequiredFields(gnss, ['latitude', 'longitude'], 'gnss', ctx);
        break;
      }
      case 'X':
        validateRequiredFields(economicOperator, ['identificationNumber'], 'economicOperator', ctx);
        break;
      case 'Y':
        validateRequiredFields({ authorisationNumber }, ['authorisationNumber'], '', ctx);
        break;
      case 'Z': {
        validateRequiredFields(address, ['street', 'postcode', 'country', 'city'], 'address', ctx);

        if (address.country && address.country.length > ADDRESS_COUNTRY_MAX_LENGTH) {
          ctx.addIssue({
            path: ['address', 'country'],
            message: 'Max 2 characters',
            code: 'custom',
          });
        }
        break;
      }
      default:
        break;
    }
  });

export const useEditGoodsLocationFormInitializer = ({ declarant, goodLocationToEdit, onHide }) => {
  const saveGoodsLocationMutation = useSaveGoodLocation();

  const onSubmit = async (values) => {
    return await saveGoodsLocationMutation.mutateAsync({
      goodLocation: values,
      declarantId: declarant._id,
    });
  };

  const buildInitialValues = (goodLocation) => ({
    _id: goodLocation?._id ?? '',
    defaultLocation: goodLocation?.defaultLocation ?? false,
    type: goodLocation?.type ?? '',
    qualifierOfIdentification: goodLocation?.qualifierOfIdentification ?? '',
    unLocode: goodLocation?.unLocode ?? '',
    unLocodeDescription: goodLocation?.unLocodeDescription ?? '',
    customsOffice: {
      referenceNumber: goodLocation?.customsOffice?.referenceNumber ?? '',
    },
    gnss: {
      latitude: goodLocation?.gnss?.latitude ?? '',
      longitude: goodLocation?.gnss?.longitude ?? '',
    },
    economicOperator: {
      identificationNumber: goodLocation?.economicOperator?.identificationNumber ?? '',
    },
    authorisationNumber: goodLocation?.authorisationNumber ?? '',
    additionalIdentifier: goodLocation?.additionalIdentifier ?? '',
    address: {
      street: goodLocation?.address?.street ?? '',
      postcode: goodLocation?.address?.postcode ?? '',
      city: goodLocation?.address?.city ?? '',
      country: goodLocation?.address?.country ?? '',
    },
    postcode: {
      postcode: goodLocation?.postcode?.postcode ?? '',
      houseNumber: goodLocation?.postcode?.houseNumber ?? '',
      country: goodLocation?.postcode?.country ?? '',
    },
    warehouses: goodLocation?.warehouses ?? [],
  });

  const form = useForm({
    initialValues: buildInitialValues(goodLocationToEdit),
    validate: zodResolver(editGoodsLocationSchema),
    validateInputOnBlur: true,
  });

  useEffect(() => {
    form.setValues(buildInitialValues(goodLocationToEdit));
  }, [goodLocationToEdit]);

  return { form, onSubmit, declarant };
};
